import React, { useState } from 'react';
import TabularImport from './TabularImport';

const API_BASE = process.env.REACT_APP_API_URL;

/**
 * BulkImportModal — wraps TabularImport in a modal and POSTs the mapped records
 * to a register's /import endpoint. Records are created as Drafts.
 *
 * props: { schema, endpoint, accent, title, getToken, onClose, onDone }
 */
export default function BulkImportModal({ schema, endpoint, accent = '#46BB89', title = 'Bulk import', getToken, onClose, onDone }) {
  const [records, setRecords] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const doImport = async () => {
    if (!records.length) return;
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ records }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Import failed');
      setResult(data);
      if (onDone) onDone(data);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, overflow: 'auto', padding: '40px 16px' }}>
      <div style={{ background: 'var(--ni-bg)', border: '1px solid var(--ni-border-soft)', borderRadius: 12, padding: 24, width: 760, maxWidth: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--ni-text-muted)', cursor: 'pointer', fontSize: 22, padding: 0, lineHeight: 1 }}>×</button>
        </div>

        {result ? (
          <div>
            <div style={{ background: accent + '11', border: `1px solid ${accent}44`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: accent }}>{result.createdCount} record{result.createdCount !== 1 ? 's' : ''} imported as Draft</div>
              {(result.errors || []).length > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: '#f59e0b' }}>
                  {result.errors.length} row{result.errors.length !== 1 ? 's' : ''} skipped:
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {result.errors.slice(0, 12).map((e, i) => <li key={i}>Row {e.row}{e.name ? ` (${e.name})` : ''}: {e.error}</li>)}
                  </ul>
                </div>
              )}
            </div>
            <button onClick={onClose} style={{ background: accent, color: '#000', border: 'none', borderRadius: 6, padding: '10px 24px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Done</button>
          </div>
        ) : (
          <>
            <p style={{ fontSize: 12, color: 'var(--ni-text-muted)', margin: '0 0 14px' }}>
              Drop or paste your register. The header row is detected automatically and columns are mapped to fields — adjust any mapping below. Each row is created as a <strong>Draft</strong> you can review and release.
            </p>
            <TabularImport schema={schema} accent={accent} onRecords={setRecords} />
            {error && <div style={{ color: '#EC2124', fontSize: 13, marginTop: 12 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button onClick={doImport} disabled={saving || !records.length}
                style={{ background: accent, color: '#000', border: 'none', borderRadius: 6, padding: '10px 24px', fontWeight: 700, fontSize: 13, cursor: (saving || !records.length) ? 'not-allowed' : 'pointer', opacity: (saving || !records.length) ? 0.6 : 1 }}>
                {saving ? 'Importing…' : `Import ${records.length || ''} record${records.length !== 1 ? 's' : ''}`}
              </button>
              <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '10px 20px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
